import React from 'react'
import clsx from 'clsx'
import { Autocomplete, TextField } from '@mui/material'

import Option from './Option'
import Tag from './Tag'

import { useUserAPI } from '../../../providers/UserAPIProvider'
import { useEnvironment } from '../../../providers/EnvironmentProvider'
import useDelayedUserSearch, {
  EXCLUDE_ME
} from '../../../hooks/useDelayedUserSearch/v2'
import useStyles from './styles'

type ChipAutocompleteProps = {
  placeholder?: string
  internal?: boolean
  markSelf?: boolean
  disabled?: boolean
  autoFocus?: boolean
  freeSolo?: boolean
  includeMe?: boolean
  wrapSelection?: boolean
} & (
  | { single: true; value: string; onChange: (value: string) => void }
  | { single: false; value: string[]; onChange: (value: string[]) => void }
)

const ChipAutocomplete: React.FC<ChipAutocompleteProps> = ({
  placeholder = 'Search for name, phone number, or email...',
  value,
  onChange,
  single,
  internal = false,
  markSelf,
  disabled,
  autoFocus,
  freeSolo = false,
  includeMe = false,
  wrapSelection = false
}) => {
  const { updateUsers } = useUserAPI()
  const { user } = useEnvironment()
  const { search, results, loading } = useDelayedUserSearch(
    !internal,
    EXCLUDE_ME(() => (includeMe ? null : user?.id))
  )

  // Coerce value to be the correct type and prevent reference changes
  const autoValue = React.useMemo(() => {
    if (single) {
      if (!value) {
        return [] as string[]
      }

      return [value] as string[]
    }

    return value as string[]
  }, [single, value])

  const shouldDisableInput = React.useMemo(
    () => single && autoValue.length > 0,
    [single, autoValue.length]
  )
  const inputRef = React.useRef<HTMLDivElement>(null)

  const classes = useStyles({ wrapSelection })

  React.useEffect(() => {
    if (results.length > 0) {
      updateUsers(results)
    }
  }, [results, updateUsers])

  return (
    <Autocomplete
      multiple
      blurOnSelect
      freeSolo={freeSolo}
      autoFocus={autoFocus}
      loading={loading}
      disabled={disabled || shouldDisableInput}
      filterOptions={x => x}
      options={results.map(result => result.id)}
      value={autoValue}
      onInputChange={(event, value, reason) => {
        search(value)
      }}
      onChange={(event, value, reason, details) => {
        let newValue = value as unknown as string[]

        // Replace current value
        if (single && newValue.length > 1) {
          newValue = [newValue[newValue.length - 1]]
        }

        // Handle onChange events differently for single
        if (!single) {
          // @ts-ignore
          onChange(newValue)
          return
        }

        onChange((newValue.length > 0 ? newValue[0] : null) as string)
      }}
      renderTags={(value: readonly string[], getTagProps) =>
        value.map((option: string, index: number) => {
          const { onDelete, ...props } = getTagProps({ index })

          const onClick = (event: React.MouseEvent<SVGElement, MouseEvent>) => {
            onDelete(event)

            // Refocus after a delay
            setTimeout(() => {
              if (inputRef.current) {
                inputRef.current.click()
              }
            }, 200)
          }

          return (
            <Tag
              {...props}
              onClick={onClick}
              option={option}
              markSelf={markSelf}
            />
          )
        })
      }
      renderInput={props => (
        <TextField
          {...props}
          inputRef={inputRef}
          InputProps={{
            ...props.InputProps,
            disableUnderline: true,
            endAdornment: null,
            className: classes.textFieldInput
          }}
          variant='standard'
          placeholder={shouldDisableInput ? '' : placeholder}
          disabled={disabled}
          className={classes.textField}
        />
      )}
      renderOption={({ className, ...props }, option) => (
        <li {...props} className={clsx(className, classes.suggestion)}>
          <Option id={option} />
        </li>
      )}
    />
  )
}

export default ChipAutocomplete
